import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, FileText, Calendar, Briefcase, Banknote, Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { getOffer, acceptOffer, rejectOffer } from '../../services/publicOfferService';
import A11yDialog from '../../components/ui/A11yDialog';

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgColor};
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: #fff;
  border-bottom: 1px solid #E8ECF0;
  padding: 16px 0;
`;

const HeaderInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Logo = styled.img`
  height: 36px;
  width: auto;
  object-fit: contain;
`;

const TenantName = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// FE-21 — DİL SEÇİCİ YOKTU. Bu sayfa kimlik doğrulaması olmadan açılır; aday
// kiracının diline kilitliydi (teklif e-postasının gövdesi de Türkçe). Kariyer
// sitesindeki (`career/CareerLayout.js`) ve dış değerlendirici sayfasındaki
// (`public-feedback/PublicFeedbackPage.js`) seçicinin AYNISI — o iki kardeş
// yüzey okunup birebir izlendi. Seçim `locales/i18n`de localStorage'a yazılır,
// `<html lang>` de güncellenir (Türkçe i/İ büyütmesi buna bağlı).
const LangSelect = styled.select`
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 6px 8px;
  background: #fff;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  flex-shrink: 0;
`;

const Main = styled.main`
  flex: 1;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px 64px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 28px 30px;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    padding: 20px 18px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 16px;
`;

const Salary = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: 16px 0 8px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #F2F4F7;
  &:first-of-type { border-top: 0; padding-top: 0; }
`;

const RowIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
  margin-top: 2px;
`;

const RowLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 2px;
`;

const RowValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 14px 20px;
  border-radius: 8px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  transition: opacity 0.15s ease;
  background: ${({ $variant }) => ($variant === 'accept' ? '#10B981'
    : $variant === 'danger' ? '#E53935' : '#fff')};
  color: ${({ $variant, theme }) => (($variant === 'accept' || $variant === 'danger')
    ? '#fff' : theme.colors.text.primary)};
  border: ${({ $variant }) => (($variant === 'accept' || $variant === 'danger')
    ? 'none' : '1px solid #E0E4E8')};
  &:hover:not(:disabled) { opacity: 0.92; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Note = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #E0E4E8;
  border-radius: 8px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  &:focus {
    outline: 0;
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 20px;
  border-radius: 10px;
  background: ${({ $tone }) => ($tone === 'success' ? '#E8F8F0'
    : $tone === 'error' ? '#FEE6E6' : '#FFF5E1')};
  color: ${({ $tone }) => ($tone === 'success' ? '#1A6E38'
    : $tone === 'error' ? '#A02525' : '#8A5500')};
  border: 1px solid ${({ $tone }) => ($tone === 'success' ? '#2EA44F'
    : $tone === 'error' ? '#E53935' : '#FFC107')};
  font-size: 14px;
  margin-bottom: 16px;

  svg { flex-shrink: 0; margin-top: 1px; }
`;

const BannerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
`;

const BannerHeadline = styled.strong`
  font-weight: 700;
`;

const BannerDetail = styled.span`
  line-height: 1.5;
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ── Onay diyaloğu kabuğu ────────────────────────────────────────────────────
// Davranış (ESC, odak tuzağı, role="dialog", perde arkasını ekran okuyucudan
// gizleme) Parti 11'in paylaşılan kabuğundan (`A11yDialog` + `dialogA11y`)
// GELİR — burada yeniden yazılmadı. Bu iki styled bileşen yalnız GÖRSEL dili
// taşır; kabuk `overlay`/`card` prop'larıyla onları kullanır.
const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2000;
`;

const DialogCard = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  /* Tek kaydırma kabı: kartın KENDİSİ. İçeride ikinci bir kaydırma kabı
     açılmaz (ev kuralı) — küçük telefonlarda özet uzun olduğunda kart kayar. */
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px 26px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.24);
  &:focus { outline: none; }

  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;

const DialogTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DialogIntro = styled.p`
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const SummaryList = styled.dl`
  margin: 0 0 18px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #F7F9FB;
  border: 1px solid #E8ECF0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 14px;
  font-size: 13.5px;

  dt {
    color: ${({ theme }) => theme.colors.text.dark.little};
    white-space: nowrap;
  }
  dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
    text-align: right;
  }
`;

const fmtDate = (d, lang) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(lang || 'tr'); } catch { return null; }
};

// Kuruş korunur (312.5 → "312,50"); tam tutar ,00 kuyruğu taşımaz (25000 → "25.000").
// Eski maximumFractionDigits: 0 kuruşu yuvarlıyordu (EO-07).
const fractionDigits = (amount) => ({
  minimumFractionDigits: Number.isInteger(Number(amount)) ? 0 : 2,
  maximumFractionDigits: 2,
});

const fmtMoney = (amount, currency, lang) => {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(lang || 'tr', {
      style: 'currency',
      currency: currency || 'TRY',
      ...fractionDigits(amount),
    }).format(amount);
  } catch {
    return `${amount} ${currency || ''}`;
  }
};

// Hisse/opsiyon adet ya da tutar olabilir — para birimi YOK; PDF'teki "Hisse / Opsiyon"
// satırıyla (AtsOfferFormatting.Amount) aynı birimsiz gösterim (inceleme bulgusu #1).
const fmtNumber = (amount, lang) => {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(lang || 'tr', fractionDigits(amount)).format(amount);
  } catch {
    return `${amount}`;
  }
};

const LANGS = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'kk', label: 'Қазақша' },
  { code: 'az', label: 'Azərbaycanca' },
];

// ════════════════════════════════════════════════════════════════════════════
// HATA SINIFLANDIRMASI (FE-21'in ikinci yarısı)
// ════════════════════════════════════════════════════════════════════════════
// Eskiden GET'in HER hatası `offer.public.invalidLink` ("Bağlantı geçersiz veya
// süresi dolmuş") yazıyordu; POST'un HER hatası `offer.public.submitError`
// ("İşlem tamamlanamadı") yazıyordu. Yani:
//   • sunucu 500 verdiğinde adaya "bağlantın ölü" deniyordu → aday İK'yı arıyor,
//     oysa 30 saniye sonra tekrar denese çalışacaktı;
//   • süresi dolmuş teklifte de "bağlantın ölü" deniyordu → aday teklifin hâlâ
//     yenilenebileceğini bilmiyor;
//   • zaten kabul edilmiş/geri alınmış teklifte de aynı cümle.
// Arka uç bu ayrımı ZATEN dönüyordu (`offer.expired`, `offer.notAcceptable`,
// `offer.notRejectable`, `offer.invalidLink`) — sayfa okumuyordu.
//
// Parti 7 kariyer sayfasında AYNI kusur sınıfını kapattı (her hata "Şirket
// sayfası bulunamadı" diyordu); buradaki tarif onun aynısıdır.
//
// `responseErrors.offer.*` sözlüğü kasten KULLANILMADI: o metinler işe alımcıya
// yazılmış durum bildirimleridir ("Bu teklif şu anda kabul edilebilir durumda
// değil"). Adayın ihtiyacı durum değil, YÖNERGE: ne oldu, şimdi ne yapmalı.
export function classifyLoadError(err) {
  const res = err?.response;
  // Ağ hatası / zaman aşımı: yanıt YOK. Bağlantı ölü DEĞİL.
  if (!res) return 'loadFailed';
  const code = res.data?.message;
  if (res.status === 404 && code === 'offer.invalidLink') return 'invalidLink';
  // 5xx ve beklenmedik her şey geçici sayılır: adaya "linkin ölü" demek
  // GERİ DÖNÜLMEZ bir yanlış yönlendirmedir (aday linki siler, İK'yı arar).
  return 'loadFailed';
}

export function classifyActionError(err) {
  const res = err?.response;
  if (!res) return 'transient';
  const code = res.data?.message;
  if (code === 'offer.expired') return 'expired';
  if (code === 'offer.notAcceptable') return 'notAcceptable';
  if (code === 'offer.notRejectable') return 'notRejectable';
  if (code === 'offer.invalidLink') return 'invalidLink';
  return 'transient';
}

// Sunucu tarafında teklifin durumu değişmiş demektir: düğmeleri göstermeye
// devam etmek adayı boşuna denemeye sürükler. Geçici hatalarda ise düğmeler
// KALMALIDIR — eski kod burada `setDone('error')` yapıp aksiyon kartını komple
// kaldırıyordu, yani tek bir 500 adayın teklifi kabul etmesini sayfa yeniden
// yüklenene kadar İMKÂNSIZ kılıyordu (kurtarılamaz hâl).
const TERMINAL_ACTION_ERRORS = new Set(['expired', 'notAcceptable', 'notRejectable', 'invalidLink']);

export default function OfferAcceptPage() {
  const { offerId, token } = useParams();
  const { t, i18n } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);      // 'invalidLink' | 'loadFailed'
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);          // 'accepted' | 'rejected'
  const [actionError, setActionError] = useState(null);  // bkz. classifyActionError
  const [rejecting, setRejecting] = useState(false);
  const [confirmingAccept, setConfirmingAccept] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await getOffer(offerId, token);
      const o = res.data?.data;
      if (!o) {
        // 200 ama gövde boş: sunucu anomalisi. "Bağlantın geçersiz" DEMEK
        // yanlış olur — bunu doğrulayan hiçbir şey yok.
        if (!silent) setLoadError('loadFailed');
        return;
      }
      setOffer(o);
      if (!silent) setLoadError(null);
    } catch (err) {
      if (!silent) setLoadError(classifyLoadError(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [offerId, token]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn, successOutcome) => {
    setSubmitting(true);
    setActionError(null);
    let kind = null;
    try {
      const res = await fn();
      // Kılıf `status:false` ile 200 dönerse de hata sınıfı sunucunun verdiği
      // anahtardan çözülür — sessizce "başarılı" sayılmaz.
      if (res.data?.status) {
        setOutcome(successOutcome);
        setRejecting(false);
      } else {
        kind = classifyActionError({ response: { status: 200, data: res.data } });
      }
    } catch (err) {
      kind = classifyActionError(err);
    } finally {
      setSubmitting(false);
      // Onay diyaloğu HER durumda kapanır: hata afişi sayfada, perdenin
      // ARKASINDA kalmamalı. Geçici hatada aksiyon düğmeleri yerinde durduğu
      // için aday hemen yeniden deneyebilir (çıkmaz yok).
      setConfirmingAccept(false);
      if (kind) {
        setActionError(kind);
        // Durum sunucuda değişmişse sayfayı GERÇEĞE eşitle: aday "süresi
        // dolmuş" uyarısını okurken hâlâ "Kabul Et" düğmesine bakmasın.
        if (TERMINAL_ACTION_ERRORS.has(kind)) load({ silent: true });
      }
    }
  };

  const handleAccept = () => runAction(() => acceptOffer(offerId, token), 'accepted');
  const handleReject = () => runAction(() => rejectOffer(offerId, token, reason || undefined), 'rejected');

  const header = (
    <Header>
      <HeaderInner>
        <Brand>
          {offer?.tenantLogoUrl && <Logo src={offer.tenantLogoUrl} alt={offer.tenantName} />}
          <TenantName>{offer?.tenantName || ''}</TenantName>
        </Brand>
        <LangSelect
          value={i18n.language || 'tr'}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          aria-label={t('offer.public.languageLabel')}
          data-testid="offer-lang-select"
        >
          {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </LangSelect>
      </HeaderInner>
    </Header>
  );

  if (loading) {
    return <Page>{header}<Main><Card>{t('offer.public.loading')}</Card></Main></Page>;
  }

  if (loadError) {
    // Sadece GERÇEK geçersiz bağlantı "geçersiz" der; gerisi yeniden denenebilir.
    const retryable = loadError !== 'invalidLink';
    return (
      <Page>
        {header}
        <Main>
          <Card>
            <Banner $tone="error" role="alert" data-testid={`load-error-${loadError}`}>
              <XCircle size={20} />
              <BannerBody>
                <BannerDetail>{t(`offer.public.error.${loadError}`)}</BannerDetail>
                {retryable && (
                  <RetryButton type="button" onClick={() => load()} data-testid="offer-retry">
                    <RefreshCw size={14} />{t('offer.public.retry')}
                  </RetryButton>
                )}
              </BannerBody>
            </Banner>
          </Card>
        </Main>
      </Page>
    );
  }

  // Hisse (bonusEquity) toplama girmez — ayrı satırda gösterilir.
  const totalCompensation = (offer.baseSalary || 0) + (offer.bonusCash || 0);
  const settled = offer.status !== 'sent';
  const showActions = !settled && !outcome;
  const contractLabel = offer.contractType
    ? `${t(`offer.contractType.${offer.contractType}`, { defaultValue: offer.contractType })}${
      offer.contractDurationMonths ? ` — ${offer.contractDurationMonths} ${t('offer.public.months')}` : ''}`
    : null;

  return (
    <Page>
      {header}

      <Main>
        {outcome === 'accepted' && (
          <Banner $tone="success" role="status" data-testid="outcome-accepted">
            <CheckCircle2 size={20} />
            <BannerBody>
              <BannerHeadline>{t('offer.public.acceptedBanner')}</BannerHeadline>
              <BannerDetail>{t('offer.public.acceptedNextSteps')}</BannerDetail>
            </BannerBody>
          </Banner>
        )}
        {outcome === 'rejected' && (
          <Banner $tone="warning" role="status" data-testid="outcome-rejected">
            <XCircle size={20} />
            <BannerBody>
              <BannerHeadline>{t('offer.public.rejectedBanner')}</BannerHeadline>
              <BannerDetail>{t('offer.public.rejectedNextSteps')}</BannerDetail>
            </BannerBody>
          </Banner>
        )}

        {/* Aksiyon hatası varken durum afişi çizilmez: ikisi de aynı olayı
            anlatır, ama hata afişi ADAYIN DENEMESİNE karşılık verir. */}
        {actionError && (
          <Banner
            $tone={actionError === 'transient' ? 'warning' : 'error'}
            role="alert"
            data-testid={`action-error-${actionError}`}
          >
            <AlertTriangle size={20} />
            <BannerBody>
              <BannerDetail>{t(`offer.public.error.${actionError}`)}</BannerDetail>
            </BannerBody>
          </Banner>
        )}

        {!actionError && !outcome && settled && (
          <Banner $tone="warning" role="status" data-testid={`settled-${offer.status}`}>
            <XCircle size={20} />
            <BannerBody>
              <BannerDetail>
                {t(`offer.public.alreadyState.${offer.status}`, { defaultValue: t('offer.public.alreadyDecided') })}
              </BannerDetail>
            </BannerBody>
          </Banner>
        )}

        <Card>
          <Subtitle>{t('offer.public.greeting', { name: offer.candidateFullName })}</Subtitle>
          <Title>{t('offer.public.title', { jobTitle: offer.jobPostingTitle })}</Title>

          <Salary>{fmtMoney(offer.baseSalary, offer.currency, i18n.language)}</Salary>
          {offer.bonusCash > 0 && (
            <Subtitle>
              {t('offer.public.bonusLine', {
                bonus: fmtMoney(offer.bonusCash, offer.currency, i18n.language),
                total: fmtMoney(totalCompensation, offer.currency, i18n.language),
              })}
            </Subtitle>
          )}
          {offer.bonusEquity > 0 && (
            <Subtitle>
              {t('offer.public.equityLine', {
                equity: fmtNumber(offer.bonusEquity, i18n.language),
              })}
            </Subtitle>
          )}

          {offer.startDate && (
            <DetailRow>
              <RowIcon><Calendar size={18} /></RowIcon>
              <div>
                <RowLabel>{t('offer.public.field.startDate')}</RowLabel>
                <RowValue>{fmtDate(offer.startDate, i18n.language)}</RowValue>
              </div>
            </DetailRow>
          )}
          {contractLabel && (
            <DetailRow>
              <RowIcon><Briefcase size={18} /></RowIcon>
              <div>
                <RowLabel>{t('offer.public.field.contractType')}</RowLabel>
                <RowValue>{contractLabel}</RowValue>
              </div>
            </DetailRow>
          )}
          {offer.responseDeadline && (
            <DetailRow>
              <RowIcon><Clock size={18} /></RowIcon>
              <div>
                <RowLabel>{t('offer.public.field.responseDeadline')}</RowLabel>
                <RowValue>{fmtDate(offer.responseDeadline, i18n.language)}</RowValue>
              </div>
            </DetailRow>
          )}
          {offer.benefitsJson && (
            <DetailRow>
              <RowIcon><Banknote size={18} /></RowIcon>
              <div>
                <RowLabel>{t('offer.public.field.benefits')}</RowLabel>
                <RowValue style={{ whiteSpace: 'pre-wrap' }}>{offer.benefitsJson}</RowValue>
              </div>
            </DetailRow>
          )}
          {offer.offerLetterFileUrl && (
            <DetailRow>
              <RowIcon><FileText size={18} /></RowIcon>
              <div>
                <RowLabel>{t('offer.public.field.letter')}</RowLabel>
                <RowValue>
                  <a href={offer.offerLetterFileUrl} target="_blank" rel="noopener noreferrer">
                    {t('offer.public.openLetter')}
                  </a>
                </RowValue>
              </div>
            </DetailRow>
          )}
        </Card>

        {showActions && (
          <Card>
            {rejecting ? (
              <>
                <RowLabel style={{ marginBottom: 8 }}>{t('offer.public.rejectReason')}</RowLabel>
                <Note
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('offer.public.rejectReasonPlaceholder')}
                  aria-label={t('offer.public.rejectReason')}
                />
                <Actions>
                  <ActionButton $variant="reject" onClick={() => setRejecting(false)} disabled={submitting}>
                    {t('common.cancel')}
                  </ActionButton>
                  {/* FE-21 — "Reddetmeyi Onayla" YEŞİL "accept" varyantındaydı:
                      reddetme ekranındaki yeşil düğme kabul sanılır. */}
                  <ActionButton $variant="danger" onClick={handleReject} disabled={submitting}>
                    {t('offer.public.confirmReject')}
                  </ActionButton>
                </Actions>
              </>
            ) : (
              <Actions>
                <ActionButton $variant="reject" onClick={() => setRejecting(true)} disabled={submitting}>
                  <XCircle size={16} />{t('offer.public.reject')}
                </ActionButton>
                {/* FE-21 — TEK DOKUNUŞTA GERİ DÖNÜLMEZ KABUL. Kabul; Employee
                    kaydı açıyor, kadroyu dolduruyor, diğer adayları kapatıyor.
                    Artık araya NE KABUL EDİLDİĞİNİ özetleyen bir onay adımı
                    giriyor — ve o adım her yönden geri alınabilir (ESC, perdeye
                    tıklama, "Vazgeç"), hiçbir veri kaybetmeden. */}
                <ActionButton
                  $variant="accept"
                  onClick={() => setConfirmingAccept(true)}
                  disabled={submitting}
                  data-testid="accept-open"
                >
                  <CheckCircle2 size={16} />{t('offer.public.accept')}
                </ActionButton>
              </Actions>
            )}
          </Card>
        )}
      </Main>

      <A11yDialog
        open={confirmingAccept}
        onClose={() => setConfirmingAccept(false)}
        closeOnOutsideClick
        overlay={DialogOverlay}
        card={DialogCard}
        overlayProps={{ 'data-testid': 'accept-confirm-overlay' }}
        cardProps={{ 'data-testid': 'accept-confirm' }}
      >
        {({ titleId }) => (
          <>
            <DialogTitle id={titleId}>{t('offer.public.confirm.title')}</DialogTitle>
            <DialogIntro>{t('offer.public.confirm.intro')}</DialogIntro>
            <SummaryList>
              <dt>{t('offer.public.confirm.position')}</dt>
              <dd>{offer.jobPostingTitle}</dd>
              <dt>{t('offer.public.confirm.baseSalary')}</dt>
              <dd>{fmtMoney(offer.baseSalary, offer.currency, i18n.language)}</dd>
              {offer.bonusCash > 0 && (
                <>
                  <dt>{t('offer.public.confirm.bonusCash')}</dt>
                  <dd>{fmtMoney(offer.bonusCash, offer.currency, i18n.language)}</dd>
                </>
              )}
              {offer.bonusEquity > 0 && (
                <>
                  <dt>{t('offer.public.confirm.bonusEquity')}</dt>
                  <dd>{fmtNumber(offer.bonusEquity, i18n.language)}</dd>
                </>
              )}
              {offer.startDate && (
                <>
                  <dt>{t('offer.public.field.startDate')}</dt>
                  <dd>{fmtDate(offer.startDate, i18n.language)}</dd>
                </>
              )}
              {contractLabel && (
                <>
                  <dt>{t('offer.public.field.contractType')}</dt>
                  <dd>{contractLabel}</dd>
                </>
              )}
            </SummaryList>
            <Actions style={{ marginTop: 0 }}>
              <ActionButton
                $variant="reject"
                onClick={() => setConfirmingAccept(false)}
                disabled={submitting}
                data-testid="accept-cancel"
              >
                {t('offer.public.confirm.cancel')}
              </ActionButton>
              <ActionButton
                $variant="accept"
                onClick={handleAccept}
                disabled={submitting}
                data-testid="accept-submit"
              >
                <CheckCircle2 size={16} />{t('offer.public.confirm.submit')}
              </ActionButton>
            </Actions>
          </>
        )}
      </A11yDialog>
    </Page>
  );
}
